// src/components/Browser/ConnectionModal.js
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FaShieldAlt, 
  FaExclamationTriangle, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaInfoCircle
} from 'react-icons/fa';
import { WalletContext } from '../../contexts/WalletContext';
import { NetworkContext } from '../../contexts/NetworkContext';
import { getChainLogo } from '../../pages/Wallet/chainLogos';
import FaviconImage from './FaviconImage';
import './ConnectionModal.css';

const ConnectionModal = ({ 
  isOpen, 
  onClose, 
  site,
  hostname,
  onApprove, 
  onReject, 
  isLoading,
  connectionType,
  securityInfo
}) => {
  const { t } = useTranslation();
  const { currentWallet } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onReject();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onReject]);
  
  if (!isOpen) return null;
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  // Function to render security status
  const renderSecurityStatus = () => {
    if (!securityInfo) return null;
    
    const { validation, isScam } = securityInfo;
    
    if (isScam) {
      return (
        <div className="connection-warning danger">
          <FaExclamationTriangle className="warning-icon danger" />
          <p>{t('browser.connectionRequest.securityThreat', 'This domain is flagged as malicious! Connecting may put your funds at risk.')}</p>
        </div>
      );
    }
    
    switch(validation) {
      case 'VALID':
        return (
          <div className="connection-security">
            <FaCheckCircle className="security-icon" />
            <p>{t('browser.connectionRequest.verifiedDomain', 'Domain verified')}</p>
          </div>
        );
      case 'INVALID':
        return (
          <div className="connection-warning">
            <FaExclamationTriangle className="warning-icon" />
            <p>{t('browser.connectionRequest.domainMismatch', 'Domain mismatch detected. Be cautious!')}</p>
          </div>
        );
      case 'UNKNOWN':
      default:
        return (
          <div className="connection-info">
            <FaInfoCircle className="info-icon" />
            <p>{t('browser.connectionRequest.unverifiedDomain', 'Unverified domain. Proceed with caution.')}</p>
          </div>
        );
    }
  };
  
  // Handle modal click to prevent overlay click propagation
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Handle swipe down to dismiss (optional touch gesture)
  const handleTouchStart = (e) => {
    // Implementation for touch gesture can be added here
  };
  
  return (
    <div className="connection-modal-overlay" onClick={onReject}>
      <div 
        className="connection-modal" 
        onClick={handleModalClick}
        onTouchStart={handleTouchStart}
      >
        <h2>{t('browser.connectionRequest.title', 'Connection Request')}</h2>
        
        <div className="site-info">
          <div className="site-favicon">
            <FaviconImage hostname={hostname} className="site-favicon-img" alt={hostname} />
          </div>
          <div className="site-details">
            <h3>{hostname}</h3>
            <p className="site-url">{site}</p>
          </div>
        </div>
        
        <div className="connection-warning">
          <FaExclamationTriangle className="warning-icon" />
          <p>{t('browser.connectionRequest.warning', 'This site wants to connect to your wallet. Only connect if you trust this site.')}</p>
        </div>
        
        {/* Security status display */}
        {renderSecurityStatus()}
        
        <div className="wallet-info">
          <h4>{t('browser.connectionRequest.connectingWallet', 'Connecting Wallet')}</h4>
          <div className="wallet-address">
            <span className="address-label">{currentWallet?.name || t('wallet.account', { number: 1 })}</span>
            <span className="address">{formatAddress(currentWallet?.address)}</span>
          </div>
        </div>
        
        <div className="network-info">
          <h4>{t('browser.connectionRequest.network', 'Network')}</h4>
          <div className="network-details">
            <div className="network-icon">
              <img 
                src={getChainLogo(currentChain?.key)} 
                alt={currentChain?.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/wallet-logo.png';
                }}
              />
            </div>
            <span>{currentChain?.name}</span>
          </div>
        </div>
        
        <div className="connection-permissions">
          <h4>{t('browser.connectionRequest.permissions', 'Permissions')}</h4>
          <div className="permission-item">
            <FaCheckCircle className="permission-icon allowed" />
            <span>{t('browser.permissions.viewAddress', 'View your wallet address')}</span>
          </div>
          <div className="permission-item">
            <FaCheckCircle className="permission-icon allowed" />
            <span>{t('browser.permissions.requestSignature', 'Request transaction signatures')}</span>
          </div>
          <div className="permission-item">
            <FaTimesCircle className="permission-icon denied" />
            <span>{t('browser.permissions.autoApprove', 'Auto-approve transactions')}</span>
          </div>
        </div>
        
        <div className="connection-type-info">
          <p>
            {connectionType === 'walletconnect' 
              ? t('browser.connectionRequest.usingWalletConnect', 'Using WalletConnect protocol') 
              : t('browser.connectionRequest.usingInjectedProvider', 'Using injected web3 provider')}
          </p>
        </div>
        
        <div className="connection-actions">
          <button 
            className="reject-button"
            onClick={onReject}
            disabled={isLoading}
          >
            {t('browser.connectionRequest.reject', 'Reject')}
          </button>
          <button 
            className="approve-button"
            onClick={onApprove}
            disabled={isLoading || (securityInfo && securityInfo.isScam)}
          >
            {isLoading ? (
              <span className="loading-spinner"></span>
            ) : null}
            {t('browser.connectionRequest.connect', 'Connect')}
          </button>
        </div>
        
        {securityInfo && securityInfo.isScam && (
          <div className="security-warning">
            <FaExclamationTriangle className="warning-icon large" />
            <p>{t('browser.connectionRequest.maliciousWarning', 
              'Connection blocked. This site has been flagged as malicious by security providers.')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionModal;